/**
 * CCA.Recaptcha.Manager
 * --------------------------------------
 * Lightweight, reusable manager for Google reCAPTCHA v2 (invisible) to be used across views.
 * Encapsulates ensure/render/execute/reset and exposes a Promise-based API so views can remain
 * framework-agnostic regarding reCAPTCHA specifics.
 *
 * Installation contracts
 * - The page must load the Google API with explicit render mode:
 *   <script async defer src="https://www.google.com/recaptcha/api.js?render=explicit"></script>
 * - Your template must include a container element with the site key, e.g.:
 *   <div id="contact-form-recaptcha" class="g-recaptcha" data-sitekey="YOUR_SITE_KEY" data-size="invisible"></div>
 *
 * Options
 * - containerSelector: string (CSS selector) => container to render the widget into (default: '#contact-form-recaptcha')
 * - size: 'invisible' | 'normal' => widget size (default: 'invisible')
 *
 * Public API
 * - constructor(options)
 * - afterRender($root?): bind to the (possibly re-rendered) DOM. Call after each view render.
 * - ensure(): Promise<void> resolves when the API is ready and the widget is rendered.
 * - executeAndGetToken(): Promise<string> resolves with the token; rejects on not-ready/error/expired.
 * - reset(): reset the widget; call if you reuse the same instance without re-rendering the DOM.
 *
 * Usage example
 *   var mgr = new RecaptchaManager({ containerSelector: '#contact-form-recaptcha' });
 *   mgr.afterRender(this.$el); // call after your view renders
 *   mgr.executeAndGetToken()
 *     .then(function (token) { doSomethingWith(token); })
 *     .catch(function (err) { handleRecaptchaError(err); });
 */

define("CCA.Recaptcha.Manager", [], function () {
  "use strict";

  function RecaptchaManager(options) {
    this.options = options || {};
    this.containerSelector =
      this.options.containerSelector || "#contact-form-recaptcha";
    this.siteKey = this.options.siteKey;
    this.size = this.options.size || "invisible";
    this._widgetId = null;
    this._token = null;
    this._containerEl = null;
    this._pending = null; // { resolve, reject }
  }

  RecaptchaManager.prototype.afterRender = function (root$) {
    // Find the container inside an optional root (e.g., a Backbone view $el)
    var el = root$
      ? root$.find(this.containerSelector)[0]
      : document.querySelector(this.containerSelector);
    this._containerEl = el || null;
    this._widgetId = null; // DOM may have been replaced; force re-render
    this._token = null;
    this.ensure();
  };

  RecaptchaManager.prototype.ensure = function () {
    var self = this;
    if (!this._containerEl) return Promise.resolve();
    if (window.grecaptcha && window.grecaptcha.render) {
      if (this._widgetId === null) {
        this._render();
      }
      return Promise.resolve();
    }
    return new Promise(function (resolve) {
      var tries = 0;
      var iv = setInterval(function () {
        tries++;
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(iv);
          if (self._widgetId === null) self._render();
          resolve();
        } else if (tries > 40) {
          // ~4s
          clearInterval(iv);
          resolve();
        }
      }, 100);
    });
  };

  RecaptchaManager.prototype._render = function () {
    if (!this._containerEl || !window.grecaptcha) return;
    var self = this;
    this._widgetId = window.grecaptcha.render(this._containerEl, {
      sitekey: this.siteKey,
      size: this.size,
      callback: function callback(token) {
        self._onSuccess(token);
      },
      "expired-callback": function expiredCallback() {
        self._onExpired();
      },
      "error-callback": function errorCallback() {
        self._onError();
      },
    });
  };

  RecaptchaManager.prototype.executeAndGetToken = function () {
    var self = this;
    return this.ensure().then(function () {
      if (self._widgetId === null || !window.grecaptcha) {
        return Promise.reject(new Error("recaptcha_not_ready"));
      }
      return new Promise(function (resolve, reject) {
        self._pending = { resolve: resolve, reject: reject };
        window.grecaptcha.execute(self._widgetId);
      });
    });
  };

  RecaptchaManager.prototype.reset = function () {
    if (
      this._widgetId !== null &&
      window.grecaptcha &&
      window.grecaptcha.reset
    ) {
      window.grecaptcha.reset(this._widgetId);
    }
    this._token = null;
  };

  RecaptchaManager.prototype._onSuccess = function (token) {
    this._token = token;
    if (this._pending && this._pending.resolve) {
      this._pending.resolve(token);
    }
    this._pending = null;
  };

  RecaptchaManager.prototype._onExpired = function () {
    this._token = null;
    if (this._pending && this._pending.reject) {
      this._pending.reject(new Error("recaptcha_expired"));
    }
    this._pending = null;
  };

  RecaptchaManager.prototype._onError = function () {
    this._token = null;
    if (this._pending && this._pending.reject) {
      this._pending.reject(new Error("recaptcha_error"));
    }
    this._pending = null;
  };

  return RecaptchaManager;
});
